import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, useTheme } from '@mui/material';
import { Dispatch, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Item } from '../../../../app/services/api/generated';
import ItemRow from './ItemRow';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import CustomPagination from '../../../../components/forms/Pagination';
import { SetStateAction } from 'react';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../../components/shared/EmptyTable';

type Props = {
  list: Item[];
  handleSort: (orderBy: keyof Item) => void;
  orderBy: string;
  order: 'asc' | 'desc';
  isLoading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  maxPages: () => number;
  itemCount: number | undefined;
  onCreateNew: () => void;
  onClear: () => void;
  searchTerm: string;
  hasAnyReadPermission: boolean;
}

const AllItemsList: FC<Props> = ({ list, handleSort, orderBy, order, isLoading, page, setPage, maxPages, itemCount, onCreateNew, onClear, searchTerm, hasAnyReadPermission }) => {
  const { t } = useTranslation('products');
  const theme = useTheme();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  if (itemCount == 0 && !isLoading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={'Items'}
      searchTerm={searchTerm}
      onClear={onClear}
      onAdd={onCreateNew}
      onAddIsCreating={false}
      addItemText={t('createNew')}
      onAddIsDisabled={false} />;
  }

  return (
    <>
      <TableHeader>
        Items
        {(itemCount ?? 0) > 0 && <Pill text={`${itemCount} Items`}></Pill>}
      </TableHeader>
      {isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={1000} /> :
        <TableContainer>
          <Table sx={{ minWidth: '1000px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                    active={orderBy === 'itemNumber'}
                    direction={order}
                    onClick={() => handleSort('itemNumber')}
                  >
                    {t('itemNumber')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    sx={{ '.MuiTableSortLabel-icon': { m: 0 } }}
                    active={orderBy === 'description'}
                    direction={order}
                    onClick={() => handleSort('description')}
                  >
                    {t('description')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('type')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('category')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('orderableUom')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('uom')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('costPrice')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <ItemRow key={row.id}
                  item={row}
                  currencyCode={currencyCode}
                  hasAnyReadPermission={hasAnyReadPermission}
                />
              ))}
              <EmptyRows numberOfCurrentRows={list.length}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={page}
        setPage={setPage}
        maxPages={maxPages()}
        breakpointForChangeDisplay={120000} />
    </>
  );
};

export default AllItemsList;